import { useState } from 'react';

import { useCreatePendency } from '../hooks/use-pendencies';
import { useToastStore } from '../stores/toast-store';

import { CapabilityGate } from './CapabilityGate';

export interface PendencyCreateModalProps {
  /** Opcional: na Tela 3 (caixa do atendente) a pendência pode ser avulsa. */
  conversationId?: string;
  open: boolean;
  onClose: () => void;
}

type Priority = 'low' | 'medium' | 'high' | 'urgent';

const PRIORITIES: { value: Priority; label: string }[] = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

/**
 * PendencyCreateModal — criação de pendência (ADR-109) na Conversa em Curso.
 *
 * Gated por capability `pendencies` (Solo+). `source` fixo em `manual` (criada
 * pelo atendente). `deadline` opcional convertida de datetime-local → ISO.
 */
export function PendencyCreateModal({
  conversationId,
  open,
  onClose,
}: PendencyCreateModalProps): JSX.Element | null {
  const create = useCreatePendency();
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [deadline, setDeadline] = useState('');
  const [description, setDescription] = useState('');

  if (!open) return null;

  const handleSubmit = (): void => {
    create.mutate(
      {
        ...(conversationId !== undefined && { conversationId }),
        title,
        priority,
        source: 'manual',
        ...(deadline.length > 0 && { deadline: new Date(deadline).toISOString() }),
        ...(description.length > 0 && { description }),
      },
      {
        onSuccess: () => {
          onClose();
          setTitle('');
          setPriority('medium');
          setDeadline('');
          setDescription('');
          useToastStore.getState().push({
            variant: 'success',
            title: 'Pendência criada',
            description: 'Adicionada às suas pendências.',
            durationMs: 3500,
          });
        },
        onError: (err) => {
          useToastStore.getState().push({
            variant: 'error',
            title: 'Falha ao criar pendência',
            description: err instanceof Error ? err.message : 'Erro desconhecido',
            durationMs: 6000,
          });
        },
      },
    );
  };

  return (
    <CapabilityGate capability="pendencies">
      <div
        role="dialog"
        aria-label="Nova pendência"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div
          className="w-full max-w-lg rounded-lg p-6 shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h2 className="mb-4 text-lg font-semibold">Nova pendência</h2>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Título</span>
            <input
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={title}
              onChange={(e): void => setTitle(e.target.value)}
              disabled={create.isPending}
              maxLength={200}
              placeholder="Ex.: retornar ligação após verificar contrato"
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Prioridade</span>
            <select
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={priority}
              onChange={(e): void => setPriority(e.target.value as Priority)}
              disabled={create.isPending}
            >
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Prazo (opcional)</span>
            <input
              type="datetime-local"
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={deadline}
              onChange={(e): void => setDeadline(e.target.value)}
              disabled={create.isPending}
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Descrição (opcional)</span>
            <textarea
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              rows={3}
              value={description}
              onChange={(e): void => setDescription(e.target.value)}
              disabled={create.isPending}
              maxLength={2000}
            />
          </label>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
              onClick={onClose}
              disabled={create.isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSubmit}
              disabled={create.isPending || title.trim().length === 0}
            >
              {create.isPending ? 'Criando…' : 'Criar pendência'}
            </button>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
